/**
 * Production Error Sanitizer
 * Removes sensitive information from errors before logging to console
 */

// Sensitive patterns to remove from error messages
const SENSITIVE_PATTERNS = [
  // Firebase project IDs
  /projects\/[a-zA-Z0-9-]+\/databases/g,
  // Session IDs and tokens
  /gsessionid=[a-zA-Z0-9_-]+/g,
  /SID=[a-zA-Z0-9_-]+/g,
  /RID=\d+/g,
  /AID=\d+/g,
  // API keys and tokens
  /[?&](key|token|auth)=[a-zA-Z0-9_-]+/g,
  // Full URLs with sensitive parameters
  /https:\/\/firestore\.googleapis\.com\/[^\s]+/g,
  // Database names
  /databases\/\([^)]+\)/g,
];

// Error types that should be suppressed entirely in production
const SUPPRESS_ERROR_TYPES = [
  'NS_BINDING_ABORTED',
  'Write/channel',
  'Listen/channel',
  'connection aborted',
  'network error',
];

/**
 * Sanitizes error messages by removing sensitive information
 */
export function sanitizeErrorMessage(message: string): string {
  if (!message) return message;

  let sanitized = message;

  // Replace sensitive patterns
  SENSITIVE_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  });

  return sanitized;
}

/**
 * Checks if an error should be suppressed entirely in production
 */
export function shouldSuppressError(error: any): boolean {
  if (!import.meta.env.PROD) {
    return false; // Don't suppress in development
  }

  const errorMessage = error?.message || String(error) || '';
  
  return SUPPRESS_ERROR_TYPES.some(type => 
    errorMessage.toLowerCase().includes(type.toLowerCase())
  );
}

/**
 * Safe console.error that sanitizes messages in production
 */
export function safeConsoleError(message: string, ...args: any[]) {
  if (import.meta.env.PROD) {
    // In production, sanitize the message and suppress network errors
    if (shouldSuppressError({ message })) {
      return; // Suppress entirely
    }
    
    const sanitizedMessage = sanitizeErrorMessage(message);
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'string') {
        return sanitizeErrorMessage(arg);
      }
      if (arg && typeof arg === 'object') {
        // Don't log complex objects in production to avoid leaking data
        return '[Object]';
      }
      return arg;
    });
    
    console.error(sanitizedMessage, ...sanitizedArgs);
  } else {
    // In development, log everything normally
    console.error(message, ...args);
  }
}

/**
 * Safe console.warn that sanitizes messages in production
 */
export function safeConsoleWarn(_message: string, ..._args: any[]) {
  if (import.meta.env.PROD) {
  } else {
  }
}

/**
 * Safe console.log for production (generally should be avoided in production)
 */
export function safeConsoleLog(_message: string, ..._args: any[]) {
  if (import.meta.env.PROD) {
  } else {
  }
}

/**
 * Global error handler that sanitizes unhandled errors
 */
export function setupGlobalErrorHandler() {
  // Override the default error handlers
  const originalError = console.error;
  const originalWarn = console.warn;
  
  if (import.meta.env.PROD) {
    console.error = (...args) => {
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          if (shouldSuppressError({ message: arg })) {
            return; // Skip this argument
          }
          return sanitizeErrorMessage(arg);
        }
        if (arg && typeof arg === 'object') {
          return '[Object]'; // Don't log objects in production
        }
        return arg;
      }).filter(Boolean);
      
      if (sanitizedArgs.length > 0) {
        originalError(...sanitizedArgs);
      }
    };

    console.warn = (...args) => {
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          if (shouldSuppressError({ message: arg })) {
            return; // Skip this argument
          }
          return sanitizeErrorMessage(arg);
        }
        if (arg && typeof arg === 'object') {
          return '[Object]';
        }
        return arg;
      }).filter(Boolean);
      
      if (sanitizedArgs.length > 0) {
        originalWarn(...sanitizedArgs);
      }
    };
  }

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (import.meta.env.PROD && shouldSuppressError(event.reason)) {
      event.preventDefault(); // Prevent the error from being logged
      return;
    }
    
    if (import.meta.env.PROD) {
      const sanitizedError = sanitizeErrorMessage(String(event.reason));
      console.error('Unhandled promise rejection:', sanitizedError);
      event.preventDefault();
    }
  });

  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    if (import.meta.env.PROD && shouldSuppressError(event.error)) {
      event.preventDefault();
      return;
    }
    
    if (import.meta.env.PROD) {
      const sanitizedError = sanitizeErrorMessage(event.message);
      console.error('Unhandled error:', sanitizedError);
      event.preventDefault();
    }
  });
}
